import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

/**
 * Les questions de la salle, côté conférence — voir
 * docs/conferences/seminaire-QUESTIONS-SALLE.md.
 *
 * Trois lectures, trois usages :
 *  - `useSalleCompte`  → le compteur du prompteur (des nombres, jamais le texte) ;
 *  - `useSalleResultat` → la slide « La salle » (fréquence, mots, synthèse) ;
 *  - `useSalleLien`    → le QR de la slide 3, dont l'URL porte le code.
 *
 * ⚠️ PAS `AdminAxios` ICI, ET C'EST LE POINT IMPORTANT. Son intercepteur
 * renvoie sur `/admin` au premier 401 : une expiration en séance enverrait le
 * prompteur — ou le DIAPORAMA PROJETÉ — sur un écran de connexion, au milieu
 * d'une phrase. Ici, une erreur ne fait jamais rien d'autre que laisser la
 * dernière valeur connue à l'écran, avec une pastille discrète.
 * (Le jeton dure 3 h depuis le 19/09 et se renouvelle de lui-même — voir plus
 * bas. La 401 ne devrait plus arriver ; on la gère quand même.)
 *
 * ⚠️ Le compteur du prompteur a un second rôle, non affiché : il interroge
 * Render toutes les 15 s pendant toute la conférence, ce qui EMPÊCHE L'INSTANCE
 * GRATUITE DE SE RENDORMIR. Sans lui, la slide « La salle » réveille un backend
 * endormi devant la salle (5 min le 18/09). Ne pas le couper « pour économiser ».
 */

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";

// Le réveil de Render peut prendre des minutes : mieux vaut attendre qu'échouer.
const DELAI_MAX = 90000;

const CLE_SESSION = "salle:session";

/**
 * La session en cours — `seminaire` le jour J, `repetition` pour les essais.
 *
 * TROIS SOURCES, DANS CET ORDRE :
 *  1. `?salle=repetition` sur l'adresse de l'onglet — le plus explicite ;
 *  2. ce qu'un onglet a déjà posé (`localStorage`, partagé par le diaporama et
 *     le prompteur, qui sont deux onglets de la même origine) ;
 *  3. `parDefaut` — la session déclarée par la conférence
 *     (`data/conferences/index.js`, champ `salle`).
 *
 * Le point 2 existe pour une raison précise : sans lui, il faudrait penser à
 * mettre `?salle=repetition` sur LES DEUX onglets, et l'oubli ne se verrait
 * qu'à l'écran — un nuage de mots vide, ou pire, les questions d'une
 * répétition projetées le jour J. Poser le paramètre sur un onglet suffit.
 */
export function sessionSalle(parDefaut = "seminaire") {
    let depuisUrl = null;
    try {
        depuisUrl = new URLSearchParams(window.location.search).get("salle");
    } catch { /* jamais en pratique */ }

    try {
        if (depuisUrl) {
            localStorage.setItem(CLE_SESSION, depuisUrl);
            return depuisUrl;
        }
        return localStorage.getItem(CLE_SESSION) || parDefaut || "seminaire";
    } catch {
        // Stockage bloqué : l'URL fait foi, et sinon ce que dit la conférence.
        return depuisUrl || parDefaut || "seminaire";
    }
}

function lireJeton() {
    try { return localStorage.getItem("adminToken"); } catch { return null; }
}

function entete() {
    const jeton = lireJeton();
    return jeton ? { Authorization: `Bearer ${jeton}` } : {};
}

/** Secondes restantes avant expiration du jeton, ou null s'il est illisible. */
function resteAvantExpiration(jeton) {
    try {
        const charge = JSON.parse(atob(jeton.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return charge.exp ? charge.exp - Math.floor(Date.now() / 1000) : null;
    } catch {
        return null;
    }
}

/**
 * Prolonge la session admin AVANT qu'elle expire.
 *
 * ⚠️ LE PROBLÈME QU'IL RÉSOUT EST UN PROBLÈME DE SCÈNE. Le diaporama et le
 * prompteur s'ouvrent avant l'accueil, et la slide « La salle » arrive à la
 * 47e minute : à l'échéance, elle n'aurait plus rien à lire, et le prompteur,
 * qui passe par `AdminAxios` pour le reste, serait renvoyé sur l'écran de
 * connexion pendant qu'Adrien parle.
 *
 * DEUX PARADES, ET ELLES SE COMPLÈTENT : le jeton dure maintenant 3 h
 * (`ADMIN_EXPIRATION_MINUTES`), ce qui couvre largement une conférence ; et
 * il se renouvelle ici, au battement du compteur (toutes les 15 s), ce qui
 * couvre le cas d'un onglet ouvert depuis la répétition du matin. Les deux
 * onglets en profitent (même origine, même `localStorage`).
 */
const SEUIL_RENOUVELLEMENT = 20 * 60; // 20 min avant la fin
let renouvellementEnCours = false;

async function entretenirJeton() {
    const jeton = lireJeton();
    if (!jeton || renouvellementEnCours) return;

    const reste = resteAvantExpiration(jeton);
    if (reste == null || reste > SEUIL_RENOUVELLEMENT || reste <= 0) return;

    renouvellementEnCours = true;
    try {
        const { data } = await axios.get(`${API}/admin/refresh-token`, {
            headers: { Authorization: `Bearer ${jeton}` },
            timeout: DELAI_MAX,
        });
        if (data?.token) localStorage.setItem("adminToken", data.token);
    } catch {
        // Échec : on retentera au battement suivant, il reste 20 minutes.
    } finally {
        renouvellementEnCours = false;
    }
}

/**
 * GARDE LE BACKEND ÉVEILLÉ pendant toute la conférence.
 *
 * LE PROBLÈME. Render (offre gratuite) endort l'instance après 15 minutes sans
 * requête, et met jusqu'à 5 minutes à repartir — observé le 18/09. Ça s'est
 * réveillé tout seul tant que le compteur battait toutes les 15 s ; depuis
 * qu'il ne bat plus (19/09), il faut autre chose.
 *
 * DEUX DÉCLENCHEURS, ET LE SECOND N'EST PAS DU LUXE :
 *  1. **chaque changement de slide** (idée d'Adrien). C'est le bon signal :
 *     il suit le rythme réel de la conférence, une slide toutes les 1 à
 *     2 minutes, sans qu'aucun minuteur ne tourne. Limité à un ping par
 *     `minDelai` pour qu'une navigation rapide en répétition n'en fasse pas
 *     une rafale.
 *  2. **un filet, toutes les 10 minutes**, parce que le premier déclencheur a
 *     deux trous : l'installation avant le début (aucune slide ne change), et
 *     surtout les questions-réponses, où la slide 35 peut rester affichée
 *     vingt minutes — c'est exactement là que le panneau des questions
 *     interroge le backend. Dix minutes, c'est sous les quinze de Render.
 *
 * ⚠️ Ce n'est PAS un rafraîchissement de données : `/api/salle/ping` ne lit
 * rien, ne renvoie rien, et ne demande aucun jeton. Rien n'est réaffiché.
 */
export function useReveilBackend(slide, { minDelai = 30000, filet = 600000 } = {}) {
    const dernier = useRef(0);

    const pinger = useCallback((force = false) => {
        const maintenant = Date.now();
        if (!force && maintenant - dernier.current < minDelai) return;
        dernier.current = maintenant;
        // Rien à attendre ni à afficher : un échec veut dire que le backend
        // dort encore, et le ping suivant le retrouvera.
        axios.get(`${API}/api/salle/ping`, { timeout: DELAI_MAX }).catch(() => { });
    }, [minDelai]);

    // Au changement de slide — et à l'ouverture du prompteur, qui est le
    // moment où l'on veut justement que le réveil commence.
    useEffect(() => { pinger(); }, [slide, pinger]);

    useEffect(() => {
        const t = setInterval(() => pinger(true), filet);
        return () => clearInterval(t);
    }, [pinger, filet]);
}

/**
 * Interroge une route admin de la salle, en boucle.
 *
 * `setTimeout` récursif et non `setInterval` : sur un réseau lent — ou pendant
 * le réveil de Render — les requêtes d'un intervalle s'empileraient les unes
 * sur les autres. Ici, la suivante part quand la précédente est retombée.
 */
function useLecture(chemin, { intervalle = null, actif = true, session: demandee = null } = {}) {
    const [donnees, setDonnees] = useState(null);
    const [erreur, setErreur] = useState(false);
    const [expire, setExpire] = useState(false);
    const [chargement, setChargement] = useState(actif);
    const minuteur = useRef(null);
    const vivant = useRef(true);

    // La session vient de la conférence quand l'appelant la connaît (le
    // prompteur la reçoit en prop) ; `?salle=` et le localStorage priment.
    const session = sessionSalle(demandee);

    const relire = useCallback(async () => {
        // Avant de lire : prolonger la session si elle approche de sa fin.
        await entretenirJeton();
        try {
            const { data } = await axios.get(`${API}${chemin}`, {
                params: { session },
                headers: entete(),
                timeout: DELAI_MAX,
            });
            if (!vivant.current) return;
            setDonnees(data);
            setErreur(false);
            setExpire(false);
        } catch (e) {
            // On garde la dernière valeur connue : un écran qui se vide en
            // séance est pire qu'un écran d'il y a quinze secondes.
            if (!vivant.current) return;
            setErreur(true);
            // 401 = session admin perdue. C'est le seul échec qu'Adrien peut
            // corriger lui-même (se reconnecter sur /admin dans un 3e onglet),
            // et il doit donc se distinguer d'une panne de réseau.
            setExpire(e?.response?.status === 401);
        } finally {
            if (vivant.current) setChargement(false);
        }
    }, [chemin, session]);

    useEffect(() => {
        vivant.current = true;
        if (!actif) return undefined;

        let arrete = false;
        const tour = async () => {
            await relire();
            if (arrete || !intervalle) return;
            minuteur.current = setTimeout(tour, intervalle);
        };
        tour();

        return () => {
            arrete = true;
            vivant.current = false;
            if (minuteur.current) clearTimeout(minuteur.current);
        };
    }, [relire, intervalle, actif]);

    return { donnees, erreur, expire, chargement, relire, session };
}

/** Le compteur du prompteur. Toutes les 15 s — voir l'avertissement en tête. */
export function useSalleCompte(intervalle = 15000, session = null) {
    const { donnees, erreur, expire, relire, session: effective } = useLecture("/api/salle/compte", { intervalle, session });
    return {
        envois: donnees?.envois ?? null,
        questions: donnees?.questions ?? null,
        syntheseSur: donnees?.synthese_sur ?? null,
        syntheseAudio: donnees?.synthese_audio === true,
        generation: donnees?.generation || null,
        relire,
        erreur,
        expire,
        session: effective,
    };
}

/**
 * Lance la synthèse et sa voix — le bouton du prompteur.
 *
 * Le backend rend la main tout de suite et travaille en fond (20 à 60 s) : le
 * `/compte` du prompteur passe alors en « en cours », puis affiche le nombre
 * de questions vues. Voir `salle.py`, section « La synthèse, produite par le
 * backend », pour la raison de ce découpage.
 */
export async function genererSynthese(session = sessionSalle()) {
    await entretenirJeton();
    const { data } = await axios.post(
        `${API}/api/salle/synthese/generer`,
        { session },
        { headers: { ...entete(), "Content-Type": "application/json" }, timeout: DELAI_MAX },
    );
    return data;
}

/**
 * Les questions classées, pour le prompteur pendant les questions-réponses.
 *
 * Lue à la demande, jamais en boucle : c'est la seule route qui rende le TEXTE
 * des questions, et le diaporama ne la touche pas.
 */
export function useSalleQuestions({ actif = false, session = null } = {}) {
    const { donnees, erreur, chargement, relire } = useLecture("/api/salle/questions", { actif, session });
    return { classement: donnees, erreur, chargement, relire };
}

/**
 * La slide « La salle ». UNE SEULE LECTURE, à l'affichage de la slide.
 *
 * ⚠️ PAS DE RAFRAÎCHISSEMENT AUTOMATIQUE, c'est une décision (19/09) : ce qui
 * est projeté ne doit pas bouger tout seul pendant qu'Adrien parle par-dessus.
 * L'écran est figé au moment où il l'affiche, et c'est le bouton du prompteur
 * — une action humaine — qui décide de ce qu'il contient. Si une question
 * arrive après, elle n'y est pas : c'est voulu, et le prompteur le dit
 * (« synth. +3 »).
 *
 * `relire` permet de rafraîchir volontairement, jamais en boucle.
 */
export function useSalleResultat({ actif = true } = {}) {
    const { donnees, erreur, expire, chargement, relire, session } = useLecture("/api/salle/resultat", {
        actif,
    });
    return { resultat: donnees, erreur, expire, chargement, relire, session };
}

/**
 * Le MP3 de la synthèse, chargé une fois, en blob.
 *
 * ⚠️ POURQUOI PAS UN `<audio src>` DIRECT : la route est protégée par le jeton
 * admin, et une balise `audio` ne peut pas porter d'en-tête `Authorization`.
 * On récupère donc les octets avec le jeton, et on en fait une URL locale.
 */
export function useSyntheseAudio({ actif = false, session = null } = {}) {
    const [url, setUrl] = useState(null);
    const sessionCourante = session || sessionSalle();

    useEffect(() => {
        if (!actif) return undefined;
        let vivant = true;
        let objet = null;

        (async () => {
            await entretenirJeton();
            try {
                const { data } = await axios.get(`${API}/api/salle/synthese/audio`, {
                    params: { session: sessionCourante },
                    headers: entete(),
                    responseType: "blob",
                    timeout: DELAI_MAX,
                });
                if (!vivant) return;
                objet = URL.createObjectURL(data);
                setUrl(objet);
            } catch {
                // 404 tant que la voix n'est pas générée : Adrien lit le texte.
                if (vivant) setUrl(null);
            }
        })();

        return () => {
            vivant = false;
            if (objet) URL.revokeObjectURL(objet);
        };
    }, [actif, sessionCourante]);

    return url;
}

/**
 * L'URL du QR de la slide 3. Lue au dernier moment, jamais écrite dans le deck :
 * les données des slides partent dans le bundle public, le code de la salle non.
 * Une seule lecture, mais réarmée à chaque affichage de la slide.
 */
export function useSalleLien({ actif = true } = {}) {
    const { donnees, erreur, expire, chargement, relire, session } = useLecture("/api/salle/lien", { actif });
    return { lien: donnees, erreur, expire, chargement, relire, session };
}
